import React from 'react';

const Privacy = () => {
  return (
    <div
    style={{
      marginTop:'15vh'
    }}
    className="max-w-4xl mx-auto p-6 bg-white">
      {/* Header */}
      <h1 className="text-3xl font-bold mb-4">PRIVACY POLICY</h1>
      <p className="mb-8">Last updated December 19, 2024</p>

      {/* Introduction */}
      <div className="mb-8">
        <p className="mb-4">
          This Privacy Notice for JoySure Innovator private limited ('we', 'us', or 'our'), describes how and why we might access, collect, store, use, and/or share ('process') your personal information when you use our services ('Services'), including when you:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Visit our website at https://www.clubnjoy.com, or any website of ours that links to this Privacy Notice</li>
          <li>Download and use our mobile application (Club N Joy), or any other application of ours that links to this Privacy Notice</li>
          <li>Use Travel packages. Buy travel packages. Enjoy the benefit. Transferable and Refundable.</li>
          <li>Engage with us in other related ways, including any sales, marketing, or events</li>
        </ul>
        <p className="mb-4">Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services.</p>
      </div>

      {/* Summary of Key Points */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">SUMMARY OF KEY POINTS</h2>
        <p className="mb-4">This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.</p>
        <ul className="list-disc pl-8 mb-4">
          <li className="mb-2"><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.</li>
          <li className="mb-2"><strong>Do we process any sensitive personal information?</strong> Some of the information may be considered 'special' or 'sensitive' in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information.</li>
          <li className="mb-2"><strong>Do we collect any information from third parties?</strong> We do not collect any information from third parties.</li>
          <li className="mb-2"><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</li>
          <li className="mb-2"><strong>In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties.</li>
          <li className="mb-2"><strong>What are your rights?</strong> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.</li>
        </ul>
      </div>

      {/* Table of Contents */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">TABLE OF CONTENTS</h2>
        <ol className="list-decimal pl-8">
          <li>WHAT INFORMATION DO WE COLLECT?</li>
          <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
          <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
          <li>WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</li>
          <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
          <li>HOW DO WE HANDLE YOUR SOCIAL LOGINS?</li>
          <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
          <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
          <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
          <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
          <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
          <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
          <li>HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</li>
        </ol>
      </div>

      {/* Section 1 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">1. WHAT INFORMATION DO WE COLLECT?</h2>
        
        <h3 className="text-xl font-bold mb-2">Personal information you disclose to us</h3>
        <p className="mb-4"><em>In Short: We collect personal information that you provide to us.</em></p>
        
        <p className="mb-4">We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.</p>
        
        <p className="mb-4"><strong>Personal Information Provided by You.</strong> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:</p>
        <ul className="list-disc pl-8 mb-4">
          <li>names</li>
          <li>phone numbers</li>
          <li>email addresses</li>
          <li>mailing addresses</li>
          <li>usernames</li>
          <li>passwords</li>
          <li>contact preferences</li>
          <li>contact or authentication data</li>
          <li>billing addresses</li>
          <li>debit/credit card numbers</li>
        </ul>

        <p className="mb-4"><strong>Sensitive Information.</strong> We do not process sensitive information.</p>

        <p className="mb-4"><strong>Social Media Login Data.</strong> We may provide you with the option to register with us using your existing social media account details, like your Facebook, X, or other social media account.</p>

        <p className="mb-4"><strong>Application Data.</strong> If you use our application(s), we also may collect the following information if you choose to provide us with access or permission:</p>
        <ul className="list-disc pl-8 mb-4">
          <li><strong>Geolocation Information.</strong> We may request access or permission to track location-based information from your mobile device, either continuously or while you are using our mobile application(s), to provide certain location-based services.</li>
          <li><strong>Mobile Device Access.</strong> We may request access or permission to certain features from your mobile device, including your mobile device's calendar, contacts, social media accounts, and other features.</li>
          <li><strong>Push Notifications.</strong> We may request to send you push notifications regarding your account or certain features of the application(s).</li>
        </ul>

        <h3 className="text-xl font-bold mb-2">Information automatically collected</h3>
        <p className="mb-4"><em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em></p>

        <p className="mb-4">We automatically collect certain information when you visit, use, or navigate the Services. This information includes:</p>
        <ul className="list-disc pl-8 mb-4">
          <li><strong>Log and Usage Data.</strong> Log and usage data is service-related, diagnostic, usage, and performance information our servers automatically collect.</li>
          <li><strong>Device Data.</strong> We collect device data such as information about your computer, phone, tablet, or other device you use to access the Services.</li>
          <li><strong>Location Data.</strong> We collect location data such as information about your device's location, which can be either precise or imprecise.</li>
        </ul>
      </div>

      {/* Section 2 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
        <p className="mb-4"><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</em></p>
        
        <p className="mb-4">We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
        <ul className="list-disc pl-8 mb-4">
          <li>To facilitate account creation and authentication and otherwise manage user accounts.</li>
          <li>To deliver and facilitate delivery of services to the user.</li>
          <li>To request feedback.</li>
          <li>To send you marketing and promotional communications.</li>
          <li>To deliver targeted advertising to you.</li>
          <li>To administer prize draws and competitions.</li>
          <li>To identify usage trends.</li>
          <li>To comply with our legal obligations.</li>
        </ul>
      </div>

      {/* Section 3 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
        <p className="mb-4"><em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em></p>

        <p className="mb-4">We may need to share your personal information in the following situations:</p>
        <ul className="list-disc pl-8 mb-4">
          <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
          <li><strong>When we use Google Maps Platform APIs.</strong> We may share your information with certain Google Maps Platform APIs (e.g. Google Maps API, Places API).</li>
        </ul>
      </div>

      {/* Section 4 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">4. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</h2>
        <p className="mb-4"><em>In Short: We are not responsible for the safety of any information that you share with third parties that we may link to or who advertise on our Services, but are not affiliated with, our Services.</em></p>
        
        <p className="mb-4">The Services may link to third-party websites, online services, or mobile applications and/or contain advertisements from third parties that are not affiliated with us and which may link to other websites, services, or applications. Accordingly, we do not make any guarantee regarding any such third parties, and we will not be liable for any loss or damage caused by the use of such third-party websites, services, or applications.</p>
      </div>

      {/* Section 5 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
        <p className="mb-4"><em>In Short: We may use cookies and other tracking technologies to collect and store your information.</em></p>
        
        <p className="mb-4">We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Notice.</p>
      </div>

      {/* Section 6 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</h2>
        <p className="mb-4"><em>In Short: If you choose to register or log in to our Services using a social media account, we may have access to certain information about you.</em></p>
        
        <p className="mb-4">Our Services offer you the ability to register and log in using your third-party social media account details. Where you choose to do this, we will receive certain profile information about you from your social media provider.</p>
      </div>

      {/* Section 7 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
        <p className="mb-4"><em>In Short: We keep your information for as long as necessary to fulfil the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>
        
        <p className="mb-4">We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law.</p>
      </div>

      {/* Section 8 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">8. DO WE COLLECT INFORMATION FROM MINORS?</h2>
        <p className="mb-4"><em>In Short: We do not knowingly collect data from or market to children under 18 years of age.</em></p>
        
        <p className="mb-4">We do not knowingly collect, solicit data from, or market to children under 18 years of age. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If you become aware of any data we may have collected from children under age 18, please contact us at vibh1990@gmail.com.</p>
      </div>

      {/* Section 9 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">9. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
        <p className="mb-4"><em>In Short: You may review, change, or terminate your account at any time.</em></p>
        
        <p className="mb-4"><strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time.</p>
        
        <p className="mb-4"><strong>Account Information:</strong> If you would at any time like to review or change the information in your account or terminate your account, you can:</p>
        <ul className="list-disc pl-8 mb-4">
          <li>Log in to your account settings and update your user account.</li>
        </ul>
      </div>

      {/* Section 10 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">10. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
        <p className="mb-4">Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ('DNT') feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected.</p>
      </div>

      <div className="text-sm text-gray-600 mt-8 pt-4 border-t">
        <p>For any questions about this Privacy Policy, please contact us at: vibh1990@gmail.com</p>
      </div>
    </div>
  );
};

export default Privacy;